import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from './queryKeys'
import type { AppSettings, TastePreset } from '@/types/settings'
import { DEFAULT_SETTINGS } from '@/types/settings'
import type { GlobalSettings, GlobalSettingsResponse, GlobalSettingsUpdate } from '@/types/globalSettings'
import { DEFAULT_GLOBAL_SETTINGS } from '@/types/globalSettings'

export function useSettingsQuery() {
  return useQuery({
    queryKey: queryKeys.settings.account(),
    queryFn: async (): Promise<AppSettings> => {
      const response = await fetch('/api/settings', { credentials: 'include' })
      const data = await response.json()
      if (data.success && data.data) {
        return { ...DEFAULT_SETTINGS, ...data.data } as AppSettings
      }
      return DEFAULT_SETTINGS
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
}

export function useGlobalSettingsQuery() {
  return useQuery({
    queryKey: queryKeys.settings.global(),
    queryFn: async (): Promise<GlobalSettingsResponse> => {
      try {
        const response = await fetch('/api/global-settings', { credentials: 'include' })
        if (!response.ok) {
          return DEFAULT_GLOBAL_SETTINGS as unknown as GlobalSettingsResponse
        }
        const data = await response.json()
        if (data.success && data.data) {
          return {
            ...DEFAULT_GLOBAL_SETTINGS,
            ...data.data,
          } as GlobalSettingsResponse
        }
      } catch {
        // ネットワークエラー等でもクラッシュさせない
      }
      return DEFAULT_GLOBAL_SETTINGS as unknown as GlobalSettingsResponse
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
}

export function useSaveSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: AppSettings) => {
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings),
      })
      if (!response.ok) throw new Error('Failed to save settings')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.account() })
    },
  })
}

/**
 * マスク表示値（••••xxxx）をAPIキーの更新から除外する
 * - マスク値はサーバー側で保存済みのため、再送不要
 * - 空文字 = 削除、プレーンテキスト = 新規設定
 */
function cleanApiKeysForSave(
  apiKeys: Record<string, string | null | undefined>
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [k, v] of Object.entries(apiKeys)) {
    if (typeof v === 'string' && !v.includes('••••')) {
      result[k] = v
    }
  }
  return result
}

/**
 * 管理者が非表示にした TTS ボイスID一覧を取得（アカウントアクセス可）。
 * 動画生成の「使用ボイス」ドロップダウンで、非表示ボイスを新規選択から除外するために使う。
 * （現在選択中のボイスは呼び出し側で例外的に残す＝グランドファザリング）
 */
export function useHiddenVoiceIdsQuery() {
  return useQuery({
    queryKey: ['tts-voices', 'hidden'],
    queryFn: async (): Promise<string[]> => {
      try {
        const response = await fetch('/api/tts/voices', { credentials: 'include' })
        if (!response.ok) return []
        const data = await response.json()
        if (data.success && data.data) return (data.data.hiddenVoiceIds ?? []) as string[]
      } catch {
        // ネットワークエラー時は空（全表示にフォールバック）
      }
      return []
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
}

export function useTastePresetsQuery() {
  return useQuery({
    queryKey: ['taste-presets'],
    queryFn: async (): Promise<TastePreset[]> => {
      const response = await fetch('/api/taste-presets', { credentials: 'include' })
      if (!response.ok) return []
      const data = await response.json()
      if (data.success && data.data) return data.data as TastePreset[]
      return []
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
}

/**
 * 管理者が設定したマスターの tastePresetOverrides を取得する。
 * 「マスター由来のオーバーライド」と「ユーザー由来のカスタム」を区別するために使う。
 */
export function useMasterTastePresetOverridesQuery() {
  return useQuery({
    queryKey: ['taste-presets', 'master'],
    queryFn: async (): Promise<Record<string, any>> => {
      const response = await fetch('/api/taste-presets/master', { credentials: 'include' })
      if (!response.ok) return {}
      const data = await response.json()
      if (data.success && data.data) return data.data as Record<string, any>
      return {}
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
}

export function useSaveGlobalSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: GlobalSettingsResponse | GlobalSettings) => {
      // APIキーのマスク値を除外してサーバーに送る
      const update: GlobalSettingsUpdate = {
        ...settings,
        apiKeys: cleanApiKeysForSave(
          (settings.apiKeys || {}) as Record<string, string | null | undefined>
        ),
      }

      const response = await fetch('/api/global-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(update),
      })
      if (!response.ok) throw new Error('Failed to save global settings')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.global() })
    },
  })
}
